import { useTranslation } from 'react-i18next';

import type { DocumentKind } from '../flow/documents';

/**
 * The camera screen as a preview shows it: the frame the applicant aims at,
 * over a DRAWN subject — a face for the selfie, a blank card for a document.
 * Drawn on purpose: a photograph of a person or of a real ID card would need a
 * licence, and a real-looking ID in a screenshot could pass for evidence.
 *
 * The shutter fires the step's event, as a taken photo would on a case.
 */
export function PreviewCamera({
  document,
  kind,
  onCapture,
}: {
  document?: DocumentKind;
  kind: 'document' | 'selfie';
  onCapture: () => void;
}) {
  const { t } = useTranslation();
  const selfie = kind === 'selfie';
  return (
    <main className="screen screen--camera" data-testid={`preview-camera-${kind}`}>
      <div className="preview-camera">
        <p className="preview-camera-title">
          {selfie
            ? t('preview.camera.selfieTitle', { defaultValue: 'Selfie capture' })
            : document
              ? t(`flow.documentChoice.kind.${document}`)
              : t('preview.camera.documentTitle', { defaultValue: 'Your document' })}
        </p>
        <div className="preview-camera-scene" aria-hidden="true">
          {selfie ? <FaceSketch /> : <CardSketch />}
          <span className={selfie ? 'preview-camera-oval' : 'preview-camera-rect'} />
        </div>
        <p className="preview-camera-hint">
          {selfie
            ? t('preview.camera.selfieHint', { defaultValue: 'Place your head within the frame' })
            : t('preview.camera.documentHint', {
                defaultValue: 'Position the document inside the frame',
              })}
        </p>
        <button
          aria-label={t('preview.camera.shutter', { defaultValue: 'Take photo' })}
          className="preview-camera-shutter"
          data-track="take_photo"
          onClick={onCapture}
          type="button"
        >
          <span aria-hidden="true" />
        </button>
      </div>
    </main>
  );
}

function FaceSketch() {
  return (
    <svg className="preview-camera-sketch preview-camera-sketch--face" viewBox="0 0 200 260">
      <ellipse cx="100" cy="250" fill="#c9cdd4" rx="90" ry="70" />
      <rect fill="#d8c3b0" height="40" rx="10" width="36" x="82" y="160" />
      <ellipse cx="100" cy="112" fill="#e3cdb8" rx="46" ry="58" />
      <path d="M54 104c0-38 20-60 46-60s46 22 46 60c-8-22-26-30-46-30s-38 8-46 30z" fill="#7a5a43" />
      <circle cx="82" cy="112" fill="#3f3f46" r="4" />
      <circle cx="118" cy="112" fill="#3f3f46" r="4" />
      <path d="M86 140q14 10 28 0" fill="none" stroke="#a8644f" strokeLinecap="round" strokeWidth="4" />
    </svg>
  );
}

function CardSketch() {
  return (
    <svg className="preview-camera-sketch" viewBox="0 0 320 200">
      <rect fill="#eef2ff" height="190" rx="14" stroke="#c7d2fe" strokeWidth="2" width="310" x="5" y="5" />
      <rect fill="#c7d2fe" height="20" rx="4" width="160" x="24" y="22" />
      <rect fill="#d4d4d8" height="90" rx="8" width="70" x="24" y="62" />
      <circle cx="59" cy="92" fill="#a1a1aa" r="16" />
      <rect fill="#a1a1aa" height="26" rx="13" width="46" x="36" y="114" />
      {[66, 88, 110, 132].map((y) => (
        <rect fill="#d4d4d8" height="10" key={y} rx="3" width={y === 132 ? 120 : 180} x="112" y={y} />
      ))}
      <rect fill="#e4e4e7" height="14" rx="3" width="272" x="24" y="164" />
    </svg>
  );
}
